import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu, ShoppingBag, Bike, ArrowUpRight } from "lucide-react";
import { resolveImg } from "@/lib/api";

const NAV = [
  { label: "Cardápio", href: "#cardapio" },
  { label: "Lançamentos", href: "#cardapio" },
  { label: "Nossa Equipe", href: "#equipe" },
  { label: "Quem Somos", href: "#quem-somos" },
  { label: "Fale Conosco", href: "#contato" },
  { label: "Dúvidas Frequentes", href: "#faq" },
];

export default function Header({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const brand = content?.brand || {};
  const header = content?.header || {};

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openLink = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-stone-950/80 backdrop-blur-xl border-b border-stone-800 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between gap-4">
        {/* Left: brand logo */}
        <a href="#hero" data-testid="brand-logo" className="flex flex-col leading-none shrink-0">
          {brand.logo ? (
            <img
              src={resolveImg(brand.logo)}
              alt={brand.name || "Logomarca"}
              className="h-16 md:h-32 w-auto object-contain shrink-0"
            />
          ) : (
            <>
              <span className="font-serif text-3xl md:text-5xl text-stone-50 tracking-tight">
                {brand.name || "Aurora"}
              </span>
              <span className="hidden sm:block text-[10px] md:text-xs tracking-[0.3em] uppercase text-amber-500/90 font-semibold mt-1">
                {brand.tagline}
              </span>
            </>
          )}
        </a>

        {/* Right: menu + CTA buttons */}
        <div className="flex items-center gap-2 sm:gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="menu-trigger"
                aria-label="Abrir menu"
                className="grid place-items-center h-11 w-11 rounded-full border border-stone-700 text-stone-100 hover:bg-stone-800 hover:border-stone-600 transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="side-menu"
              className="bg-stone-950 border-stone-800 text-stone-100 w-full sm:max-w-md p-0"
            >
              <div className="flex flex-col h-full p-8 md:p-12">
                <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
                <SheetDescription className="sr-only">Links para as seções do site</SheetDescription>
                <span className="text-xs tracking-[0.3em] uppercase text-amber-500 font-semibold">
                  {brand.tagline || "Menu"}
                </span>
                <nav className="mt-12 flex flex-col gap-1">
                  {NAV.map((item, i) => (
                    <SheetClose asChild key={item.label + i}>
                      <a
                        href={item.href}
                        data-testid={`menu-link-${i}`}
                        className="group flex items-center justify-between py-4 border-b border-stone-800 text-3xl md:text-4xl font-serif text-stone-200 hover:text-amber-500 transition-colors duration-300"
                      >
                        {item.label}
                        <ArrowUpRight className="h-6 w-6 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                      </a>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto pt-10 flex flex-col gap-3">
                  <button
                    data-testid="menu-order-btn"
                    onClick={() => openLink(header.orderButton?.url)}
                    className="w-full rounded-full border border-stone-600 text-stone-100 py-4 font-medium hover:bg-stone-800 transition-colors duration-300"
                  >
                    {header.orderButton?.label || "Peça e Retire"}
                  </button>
                  <button
                    data-testid="menu-delivery-btn"
                    onClick={() => openLink(header.deliveryButton?.url)}
                    className="w-full rounded-full bg-amber-700 text-stone-50 py-4 font-medium hover:bg-amber-600 transition-colors duration-300"
                  >
                    {header.deliveryButton?.label || "Delivery"}
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <button
            data-testid="header-order-btn"
            onClick={() => openLink(header.orderButton?.url)}
            className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-stone-600 text-stone-100 px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium hover:bg-stone-800 hover:border-stone-500 transition-colors duration-300"
          >
            <ShoppingBag className="h-4 w-4 shrink-0" />
            <span>{header.orderButton?.label || "Peça e Retire"}</span>
          </button>
          <button
            data-testid="header-delivery-btn"
            onClick={() => openLink(header.deliveryButton?.url)}
            className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-amber-700 text-stone-50 px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium hover:bg-amber-600 transition-colors duration-300"
          >
            <Bike className="h-4 w-4 shrink-0" />
            <span>{header.deliveryButton?.label || "Delivery"}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
